class LocaleSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('form');
    this.items = [...this.querySelectorAll('[data-locale-item]')];

    if (!this.form || !this.items.length) return;

    this.boundHandleOutside = this.handleOutsideClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-locale-trigger]');
      const closeBtn = item.querySelector('[data-locale-close]');

      if (!trigger) return;

      trigger.addEventListener('click', () => this.toggle(item));
      closeBtn?.addEventListener('click', () => this.close(item));
    });

    document.addEventListener('click', this.boundHandleOutside);
    document.addEventListener('keydown', this.boundHandleKeydown);
    window.addEventListener('resize', this.boundHandleResize);
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    document.removeEventListener('click', this.boundHandleOutside);
    document.removeEventListener('keydown', this.boundHandleKeydown);
    window.removeEventListener('resize', this.boundHandleResize);
  }

  toggle(item) {
    const panel = item.querySelector('[data-locale-panel]');
    const isOpen = panel?.classList.contains('is-open');

    this.closeAll(item);

    if (!isOpen) {
      this.open(item);
    }
  }

  open(item) {
    const trigger = item.querySelector('[data-locale-trigger]');
    const panel = item.querySelector('[data-locale-panel]');

    if (!trigger || !panel) return;

    item._lastFocus = document.activeElement;
    this.positionPanel(item);
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    const focusables = this.getFocusableElements(panel);
    focusables[0]?.focus();
  }

  close(item) {
    const trigger = item.querySelector('[data-locale-trigger]');
    const panel = item.querySelector('[data-locale-panel]');

    if (!trigger || !panel) return;

    panel.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');

    const lastFocus = item._lastFocus;
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    } else {
      trigger.focus();
    }
    item._lastFocus = null;
  }

  closeAll(except) {
    this.items.forEach((item) => {
      if (item === except) return;
      const panel = item.querySelector('[data-locale-panel]');
      if (panel?.classList.contains('is-open')) {
        this.close(item);
      }
    });
  }

  handleOutsideClick(event) {
    this.items.forEach((item) => {
      const panel = item.querySelector('[data-locale-panel]');
      if (panel?.classList.contains('is-open') && !item.contains(event.target)) {
        this.close(item);
      }
    });
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      const openItem = this.items.find((item) =>
        item.querySelector('[data-locale-panel]')?.classList.contains('is-open')
      );
      if (openItem) this.close(openItem);
      return;
    }

    if (event.key !== 'Tab') return;

    const openItem = this.items.find((item) =>
      item.querySelector('[data-locale-panel]')?.classList.contains('is-open')
    );
    if (!openItem) return;

    const panel = openItem.querySelector('[data-locale-panel]');
    const focusables = this.getFocusableElements(panel);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  handleResize() {
    this.items.forEach((item) => {
      const panel = item.querySelector('[data-locale-panel]');
      if (panel?.classList.contains('is-open')) {
        this.positionPanel(item);
      }
    });
  }

  positionPanel(item) {
    const trigger = item.querySelector('[data-locale-trigger]');
    const panel = item.querySelector('[data-locale-panel]');
    if (!trigger || !panel) return;

    panel.classList.remove('panel-above', 'panel-below');

    const wasOpen = panel.classList.contains('is-open');
    if (!wasOpen) panel.classList.add('is-open');

    const triggerRect = trigger.getBoundingClientRect();
    const panelHeight = panel.offsetHeight || panel.scrollHeight || 200;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const gap = 8;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openAbove = spaceBelow < panelHeight + gap && spaceAbove > spaceBelow;

    panel.classList.add(openAbove ? 'panel-above' : 'panel-below');

    if (!wasOpen) panel.classList.remove('is-open');
  }

  getFocusableElements(container) {
    if (!container) return [];
    const selectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    return [...container.querySelectorAll(selectors.join(','))]
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
  }
}

if (!customElements.get('locale-selector')) {
  customElements.define('locale-selector', LocaleSelector);
}

class HeaderElement extends HTMLElement {
  constructor() {
    super();
    this.lastHeight = 0;
    this.update = this.update.bind(this);
    this._rafId = null;
    this._isSticky = false;
    this._onScroll = this._onScroll.bind(this);
  }

  connectedCallback() {

    this.headerOuter = this.querySelector('.theme_head__shell');
    if (!this.headerOuter) return; 

    this.cleanup = rafResizeObserver(this.headerOuter, height => {
      document.body.style.setProperty('--header-height', `${height}px`);
    });

    if (this.hasAttribute('data-sticky')) {
      this._sectionEl = this.closest('.header-element');
      window.addEventListener('scroll', this._onScroll, { passive: true });
    } 
  }

  disconnectedCallback() {
    this.cleanup?.();
    window.removeEventListener('scroll', this._onScroll);
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }

  _onScroll() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const shouldStick = window.scrollY > 350;
      if (shouldStick === this._isSticky || !this._sectionEl) return;
      this._isSticky = shouldStick;
      this._sectionEl.classList.toggle('sticky-header', shouldStick);
    });
  }

  update() {
    if (!this.headerOuter) return;

    const newHeight = this.headerOuter.getBoundingClientRect().height;

    if (newHeight !== this.lastHeight) {
      this.lastHeight = newHeight;
      document.body.style.setProperty('--header-height', `${newHeight}px`);
    }
  }
}

if (!customElements.get('theme-header')) {
  customElements.define('theme-header', HeaderElement);
}

function rafResizeObserver(el, onChange) {
  if (!el) return;

  let last = 0;
  let rafId = null;

  const ro = new ResizeObserver(entries => {
    const height = entries[0].contentRect.height;

    if (height === last) return;
    last = height;

    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      onChange(height);
    });
  });

  ro.observe(el);

  return () => ro.disconnect();
}
window.whenSwiperReady = (function () {
  let ready = false;
  let queue = [];
  let checks = 0;
  const maxChecks = 40;
  const checkcascade = 250;

  function flush() {
    ready = true;
    queue.forEach(cb => cb());
    queue.length = 0;
  }

  if (window.Swiper) {
    ready = true;
  } else {
    const check = () => {
      if (window.Swiper) {
        flush();
        return;
      }

      checks += 1;
      if (checks < maxChecks) {
        setTimeout(check, checkcascade);
      }
    };
 
    window.addEventListener('theme:swiper-ready', flush, { once: true });
    check();
  }

  return function (callback) {
    if (ready) {
      callback();
    } else {
      queue.push(callback);
    }
  };
})();

function lazyInit(element, callback) {
  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      observer.disconnect();
      callback();
    }
  }, { rootMargin: '150px' });

  observer.observe(element);
}

window.whenLenisReady = (function () {
  let ready = false;
  let queue = [];
  let checks = 0;
  const maxChecks = 40;
  const checkcascade = 250;

  function run(fn) {
    if (typeof fn === 'function') {
      fn(window.lenis);
    }
  }

  function flush() {
    ready = true;
    queue.forEach(run);
    queue.length = 0;
  }

  if (window.lenis) {
    ready = true;
  } else {
    const check = () => {
      if (window.lenis) {
        flush();
        return;
      }

      checks += 1;
      if (checks < maxChecks) {
        setTimeout(check, checkcascade);
      }
    };

    window.addEventListener('theme:lenis-ready', flush, { once: true });
    check();
  }

  return function (callback) {
    if (typeof callback !== 'function') return;

    if (ready) {
      run(callback);
    } else {
      queue.push(callback);
    }
  };
})();

class CategoriesSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    window.whenSwiperReady(() => {
      this.initSwiper();
    });
  }

  initSwiper() {
    const sliderEl = this.querySelector('.category-card-grid.swiper');
    if (!sliderEl || sliderEl.swiper) return;

    const columns = Number(sliderEl.dataset.columns) || 4;

    this.swiper = new Swiper(sliderEl, {
      slidesPerView: 1.15,
      spaceBetween: 12,
      speed: 700,

      breakpoints: {
        480: { 
          slidesPerView: 2, 
          spaceBetween: 16 
        },

        750: { 
          slidesPerView: 3, 
          spaceBetween: 16 
        },

        990: { 
          slidesPerView: Math.min(columns, 4),
          spaceBetween: 18 
        },

        1200: { 
          slidesPerView: columns,
          spaceBetween: 20 
        }
      },

      pagination: {
        el: sliderEl.querySelector('.category-card-pagination'),
        clickable: true,
      },

      navigation: {
      nextEl: this.querySelector('.swiper-button-next'),
      prevEl: this.querySelector('.swiper-button-prev'),
    },

      observer: true,
      observeParents: true,
    });
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this._initialized = false;
  }
}

if (!customElements.get('categories-slider')) {
  customElements.define('categories-slider', CategoriesSlider);
}


class FeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    window.whenSwiperReady(() => {
      this.init();
    });
  }

  init() {
    const sliderEl = this.querySelector('.swiper');
    if (!sliderEl || sliderEl.swiper) return;

    const wrapper = sliderEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const slides = wrapper.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    const desktop = parseInt(this.dataset.columns, 10) || 4;
    const mobile = parseFloat(this.dataset.mobColumns, 10) || 2;
    const smallDesktop = parseInt(this.dataset.smallcolumns, 10) || 2;
    const spacing = parseInt(this.dataset.spacing, 10) || 20;

    let nextBtn = sliderEl.querySelector('.swiper-main-button-next') || sliderEl.querySelector('.swiper-button-next');
    let prevBtn = sliderEl.querySelector('.swiper-main-button-prev') || sliderEl.querySelector('.swiper-button-prev');

    if (!nextBtn || !prevBtn) {
      nextBtn = document.createElement('div');
      prevBtn = document.createElement('div');

      nextBtn.className = 'slider_button swiper-main-button-next swiper-button-next';
      prevBtn.className = 'slider_button swiper-main-button-prev swiper-button-prev';

      sliderEl.appendChild(prevBtn);
      sliderEl.appendChild(nextBtn);
    }

    this.swiper = new Swiper(sliderEl, {
      slidesPerView: mobile,
      speed: 800,
      spaceBetween: 10,
      loop: false,

      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },

      scrollbar: {
        el: sliderEl.querySelector('.collection_scroller'),
        draggable: true,
        snapOnRelease: true,
      },

      breakpoints: {
        0: { slidesPerView: 1 },
        380: { slidesPerView: 1.3 },
         640: {
          slidesPerView: 3,
          spaceBetween: 10,
        },
        900: {
          slidesPerView: smallDesktop,
          spaceBetween: spacing,
        },
        1300: {
          slidesPerView: desktop,
          spaceBetween: spacing,
        },
      },

      observer: true,
      observeParents: true,
    });
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this._initialized = false;
  }
}

customElements.define('featured-collection', FeaturedCollection);


class FeaturedBlog extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    lazyInit(this, () => {
    window.whenSwiperReady(() => this.init());
  });
  }

  init() {
    const slides = this.querySelectorAll('.featured_article.swiper-slide');
    if (slides.length <= 1) return;

    const slider = this.querySelector('.featured_block_inner');
    if (!slider) return;

    const spacing = Number(this.dataset.spacing) || 0;

    const navWrapper = this.previousElementSibling;
    const prevBtn = navWrapper?.querySelector('.swiper-button-prev');
    const nextBtn = navWrapper?.querySelector('.swiper-button-next');

    this.swiper = new Swiper(slider, {
      speed: 600,
      slidesPerView: 2,
      spaceBetween: spacing,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      pagination: {
        el: this.querySelector('.swipe--pagination'),
        clickable: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: spacing,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: spacing,
        },
        991: {
          slidesPerView: 2,
          spaceBetween: spacing,
        },
        1250: {
          slidesPerView: 3,
          spaceBetween: spacing,
        },
      },
      observer: true,
      observeParents: true,
    });
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this._initialized = false;
  }
}

customElements.define('featured-blog', FeaturedBlog);
 

class ThemeMenuDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addAccordionListeners();
  }

  addAccordionListeners() {
    const toggles = this.querySelectorAll(".accordion-toggle button");
    toggles.forEach((button) => {
      button.addEventListener("click", () => {
        const toggle = button.closest(".accordion-toggle");
        const content = toggle?.nextElementSibling;
        const isOpen = toggle.classList.contains("active");
        const plus = toggle.querySelector?.(".plus_vertical");

        this.closeSiblings(toggle);

        if (content?.classList.contains("accordion-content")) {
          toggle.classList.toggle("active", !isOpen);
          content.classList.toggle("active", !isOpen);
          plus?.classList.toggle("rot", !isOpen);

          if (isOpen) {
            this.slideUp(content);
          } else {
            this.slideDown(content);
          }
        }

        this.syncTabIndexes();
      });
    });
  }

  // Links inside a collapsed accordion are only clipped by `max-height: 0`, so they
  // stay reachable by Tab unless their tabindex is refreshed when the panel toggles.
  syncTabIndexes() {
    const panel = this.querySelector(PANEL_BRAND.wrapper) || this.closest(PANEL_BRAND.wrapper);
    if (panel && typeof refreshPanelTabIndexes === "function") {
      refreshPanelTabIndexes(panel);
    }
  }

  slideDown(el) {
    el.style.maxHeight = el.scrollHeight + "px";

    requestAnimationFrame(() => {
      const parentAccordion = el.closest(".main-accordion-content");
      if (parentAccordion) {
        parentAccordion.style.maxHeight =
          parentAccordion.scrollHeight + el.scrollHeight + "px";
      }
    });
  }

  slideUp(el) {
    el.style.maxHeight = "0";

    requestAnimationFrame(() => {
      const parentAccordion = el.closest(".accordion-content");
      if (parentAccordion && parentAccordion !== el) {
        parentAccordion.style.maxHeight = parentAccordion.scrollHeight + "px";
      }
    });
  }

  closeSiblings(currentToggle) {
    const parentWrapper =
      currentToggle.closest(".accordion-wrapper") ||
      currentToggle.parentElement.parentElement;

    const allToggles = parentWrapper.querySelectorAll(".accordion-toggle");

    allToggles.forEach((toggle) => {
      if (toggle === currentToggle) return;

      const content = toggle.nextElementSibling;

      if (content?.classList.contains("accordion-content")) {
        toggle.classList.remove("active");
        content.classList.remove("active");
        toggle.querySelector?.(".plus_vertical")?.classList.remove("rot");
        this.slideUp(content);
      }
    });
  }
} 

customElements.define("theme-menu-drawer", ThemeMenuDrawer);



const PANEL_BRAND = {
  wrapper: "[data-theme-drawer], .theme-drawer, .drawer-wrapper",
  inner: "[data-theme-drawer-inner], .theme-drawer__inner, .drawer-wrapper_inner",
  closeBtn: "[data-theme-drawer-close], .theme-drawer__close, .drawer_close_btn",
  openTrigger: "[data-theme-drawer-open], [data-panel], [data-drawer], .drawer_open_btn",
  initFlag: "data-theme-drawer-initialized",
  closeInitFlag: "closeInitialized"
};

let lastPanelTrigger = null;

const PANEL_INPUT_MODALITY_KEY = "__themeDrawerLastInputWasKeyboard";
const LEGACY_DRAWER_INPUT_MODALITY_KEY = "__drawerLastInputWasKeyboard";

if (typeof window[PANEL_INPUT_MODALITY_KEY] === "undefined") {
  window[PANEL_INPUT_MODALITY_KEY] = false;
}

if (typeof window[LEGACY_DRAWER_INPUT_MODALITY_KEY] === "undefined") {
  window[LEGACY_DRAWER_INPUT_MODALITY_KEY] = window[PANEL_INPUT_MODALITY_KEY];
}

function getAllPanels() {
  return document.querySelectorAll(PANEL_BRAND.wrapper);
}

function setInputModality(wasKeyboard) {
  window[PANEL_INPUT_MODALITY_KEY] = wasKeyboard;
  window[LEGACY_DRAWER_INPUT_MODALITY_KEY] = wasKeyboard;
}

function markKeyboardNavigation() {
  setInputModality(true);
}

function markPointerNavigation() {
  setInputModality(false);
}

document.addEventListener("keydown", (event) => {
  if (event.metaKey || event.altKey || event.ctrlKey) return;
  markKeyboardNavigation();
}, true);

document.addEventListener("pointerdown", markPointerNavigation, true);
document.addEventListener("mousedown", markPointerNavigation, true);
document.addEventListener("touchstart", markPointerNavigation, true);

function isInsideCollapsedAccordion(el) {
  // Collapsed accordions are clipped with `max-height: 0; overflow: hidden`, so their
  // children keep a layout box and pass the generic offset/rect visibility test.
  let content = el.closest(".accordion-content");
  while (content) {
    if (!content.classList.contains("active")) return true;
    content = content.parentElement?.closest(".accordion-content") || null;
  }
  return false;
}

function getPanelFocusableElements(panel) {
  return Array.from(
    panel.querySelectorAll("button, input:not([type='hidden']), a, select, textarea")
  ).filter(el => {
    if (el.hasAttribute('focus_enabled')) {
      el.setAttribute('tabindex', '0');
      return true;
    }
    if (el.disabled) return false;
    if (el.hasAttribute('tab_index_minus')) {
      el.setAttribute('tabindex', '-1');
      return false;
    }
    if (isInsideCollapsedAccordion(el)) return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  });
}

// Re-applies tabindex across an open panel: focusable items get 0, everything
// else (collapsed accordion content) is pushed out of the tab order.
function refreshPanelTabIndexes(panel) {
  if (!panel || !panel.classList.contains("active")) return;

  const focusable = new Set(getPanelFocusableElements(panel));

  panel.querySelectorAll("button, input:not([type='hidden']), a, select, textarea").forEach((el) => {
    if (el.hasAttribute('no_index_add')) return;
    el.setAttribute("tabindex", focusable.has(el) ? "0" : "-1");
  });
}

window.refreshPanelTabIndexes = refreshPanelTabIndexes;

function initializePanelBrandHooks() {
  getAllPanels().forEach((panel) => {
    panel.classList.add("theme-drawer");
    const inner = panel.querySelector(PANEL_BRAND.inner);
    if (inner) {
      inner.classList.add("theme-drawer__inner");
    }
    panel.querySelectorAll(PANEL_BRAND.closeBtn).forEach((btn) => {
      btn.classList.add("theme-drawer__close");
    });
  });
}

getAllPanels().forEach((panel) => {
  const focusableElements = getPanelFocusableElements(panel);
  focusableElements.forEach((el) => {
    if (el.hasAttribute('no_index_add')) return;
    el.setAttribute("tabindex", "-1");
  });
});

function closeAllPanels() {
  getAllPanels().forEach((panel) => {
    if (panel.__closingTimer) {
      clearTimeout(panel.__closingTimer);
      panel.__closingTimer = null;
    }
    panel.classList.remove("closing");
    panel.classList.remove("active");
    panel.classList.remove("content_fetched");
    panel.setAttribute("aria-hidden", "true");
    panel.setAttribute('inert', '');
    panel.querySelectorAll("input, button, a").forEach(el => {
      if (el.hasAttribute('no_index_add')) return;
      el.setAttribute("tabindex", "-1");
    });
  });
}


function trapPanelFocus(event, panel) {
  const focusableElements = getPanelFocusableElements(panel);
  if (!focusableElements.length) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.key === "Tab") {
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      if (lastElement) {
        lastElement.focus();
      }
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      if (firstElement) {
        firstElement.focus();
      }
    }
  }
}

function openPanel(panel, triggerButton) {
  closeAllPanels();
  if (panel) {
    if (panel.__closingTimer) {
      clearTimeout(panel.__closingTimer);
      panel.__closingTimer = null;
    }

    if (!triggerButton || !triggerButton.closest(PANEL_BRAND.wrapper)) {
      lastPanelTrigger = triggerButton;
    }

    panel.classList.remove("closing");
    panel.classList.add("active");
    panel.setAttribute("aria-hidden", "false");
    document.body.classList.add("overflow-hidden");
    //  document.querySelector(".header-element").style.zIndex = "0";

     if (panel.hasAttribute('inert')) {
      panel.removeAttribute('inert');
    }

    const focusableElements = getPanelFocusableElements(panel);
    focusableElements.forEach((el) => {
      if (el.hasAttribute('no_index_add')) return;
      el.setAttribute("tabindex", "0");
    });

    if (focusableElements.length) {
      focusableElements[0].focus();
    }

    window.whenLenisReady(lenis => {
      lenis.stop();
    });


    if (!panel.__trapFocusHandler) {
      panel.__trapFocusHandler = (event) => trapPanelFocus(event, panel);
      panel.addEventListener("keydown", panel.__trapFocusHandler);
    }
  }
}

function closePanel(panel, options = {}) {
  const shouldRestoreFocus =
    typeof options.restoreFocus === "boolean"
      ? options.restoreFocus
      : window[PANEL_INPUT_MODALITY_KEY] === true;
  const shouldAnimateClose = options.animateClose === true;

  if (panel) {
    if (panel.__closingTimer) {
      clearTimeout(panel.__closingTimer);
      panel.__closingTimer = null;
    }

    if (shouldAnimateClose) {
      panel.classList.add("closing");
      panel.__closingTimer = setTimeout(() => {
        panel.classList.remove("closing");
        panel.__closingTimer = null;
      }, 600);
    } else {
      panel.classList.remove("closing");
    }

    panel.classList.remove("active");
    panel.setAttribute("aria-hidden", "true");
    panel.setAttribute('inert', '');
    document.body.classList.remove("overflow-hidden");
    if (panel.classList.contains("mobile_filter_drawer_wrapper")) {
      setTimeout(() => {
        document.querySelector(".header-element").style.zIndex = "4";
      }, 200);
    } else if(panel.classList.contains('quick-view-wrapper')) {
     const placeholderTemplate = document.getElementById('quick-view-drawer_placeholder_template');
     const contentWrapper = panel.querySelector('.quick-view-main-inner');

      if (placeholderTemplate && contentWrapper) {
        contentWrapper.innerHTML = placeholderTemplate.innerHTML;
      }
    }

    const focusableElements = panel.querySelectorAll("input, button, a");
    focusableElements.forEach((el) => {
      if (el.hasAttribute('no_index_add')) return;
      el.setAttribute("tabindex", "-1");
    });

    if (shouldRestoreFocus && lastPanelTrigger) {
      lastPanelTrigger.focus();
      lastPanelTrigger = null;
    } else if (!shouldRestoreFocus) {
      lastPanelTrigger = null;
    }

   window.whenLenisReady(lenis => {
    lenis.start();
  });

  }
}

function initPanelOpenButtons() {
  const openBtns = document.querySelectorAll(PANEL_BRAND.openTrigger);

  openBtns.forEach((button) => {
    if (!button.hasAttribute(PANEL_BRAND.initFlag)) {
      button.setAttribute(PANEL_BRAND.initFlag, 'true');
      if (!button.hasAttribute('data-drawer-initialized')) {
        button.setAttribute('data-drawer-initialized', 'true');
      }

      button.addEventListener("click", async (event) => {
        event.preventDefault();
        const panelId =
          button.getAttribute("data-theme-drawer-open") ||
          button.getAttribute("data-panel") ||
          button.getAttribute("data-drawer");
        const panel = document.getElementById(panelId);
        if (panel) {
          openPanel(panel, button);
        }
      });
    }
  });
}

document.addEventListener("DOMContentLoaded", initPanelOpenButtons);
document.addEventListener("shopify:section:load", initPanelOpenButtons);
document.addEventListener("shopify:section:unload", initPanelOpenButtons);


function bindPanelCloseButtons() {
  document.querySelectorAll(PANEL_BRAND.closeBtn).forEach((button) => {
    if (button.dataset[PANEL_BRAND.closeInitFlag]) return; // avoid rebinding
    button.dataset[PANEL_BRAND.closeInitFlag] = "true";

    button.addEventListener("click", () => {
      const panel = button.closest(PANEL_BRAND.wrapper);
      closePanel(panel, {
        restoreFocus: window[PANEL_INPUT_MODALITY_KEY] === true,
        animateClose: true,
      });
    });
  });
}
 
document.addEventListener("DOMContentLoaded", bindPanelCloseButtons);
document.addEventListener("shopify:section:load", bindPanelCloseButtons);
document.addEventListener("collection-grid-update", bindPanelCloseButtons);


function bindPanelOverlayAndEscapeHandlers() {
  getAllPanels().forEach((panel) => {
    if (panel.dataset.panelLayerBound === "true") return;
    panel.dataset.panelLayerBound = "true";

    panel.addEventListener("click", (event) => {
      if (event.target === panel) {
        closePanel(panel, {
          restoreFocus: false,
          animateClose: true,
        });
      }
    });

    panel.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closePanel(panel, {
          restoreFocus: true,
          animateClose: true,
        });
      }
    });

    const panelInner = panel.querySelector(PANEL_BRAND.inner);
    if (panelInner) {
      panelInner.classList.add("theme-drawer__inner");
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initializePanelBrandHooks();
  bindPanelOverlayAndEscapeHandlers();
});

document.addEventListener("shopify:section:load", () => {
  initializePanelBrandHooks();
  bindPanelOverlayAndEscapeHandlers();
});

function defineElementIfNeeded(tagName, elementClass) {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, elementClass);
  }
}


class CartDrawerClose extends HTMLElement {
  constructor() {
    super();
    this.closeBtn = this.querySelector(PANEL_BRAND.closeBtn);
    this.panel = this.closest('.theme-cart-drawer-shell, .theme-drawer');
  }
 
  connectedCallback() {
    if (!this.closeBtn) return;
    this.closeBtn.addEventListener('click', this.handleClose.bind(this));
  }

  handleClose() {
    closePanel(this.panel, {
      animateClose: true,
    });
  }
}

defineElementIfNeeded('theme-cart-drawer-close', CartDrawerClose);

class FilterDrawerClose extends HTMLElement {
  connectedCallback() {
    if (typeof bindPanelCloseButtons === 'function') {
      bindPanelCloseButtons();
    }
  }  
}

if (!customElements.get('filter-drawer-close')) {
  customElements.define('filter-drawer-close', FilterDrawerClose);
}



document.addEventListener("DOMContentLoaded", () => {
  const enrichImageAltText = () => {
    const imgs = document.querySelectorAll("img:not([alt]), img[alt='']");
    imgs.forEach(img => {
    if (!img.hasAttribute("alt") || img.alt.trim() === "") {
      
      let altText = "";

      const src = img.getAttribute("src") || "";
      const fileName = src.split("/").pop().split("?")[0].split(".")[0];

      if (fileName) {
        altText = fileName
          .replace(/[-_]/g, " ")
          .replace(/\d+/g, "")             
          .trim();
      }

      if (!altText && img.closest("[aria-label]")) {
        altText = img.closest("[aria-label]").getAttribute("aria-label");
      }

      if (!altText) {
        altText = "Image";
      }

      img.setAttribute("alt", altText);
    }
    });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(enrichImageAltText, { timeout: 1200 });
  } else {
    setTimeout(enrichImageAltText, 250);
  } 
});
 
 


class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.tabButtons = [...this.querySelectorAll('.featured_tab-btn')];
    this.tabPanels  = [...this.querySelectorAll("[slot='panels'] .featured_tab-panel")];

    this.activate(0);

    this.tabButtons.forEach((btn, i) => {
      btn.addEventListener("click", () => this.activate(i));
    });

    const globalPrev = this.querySelector('.swiper-tab_global-prev');
    const globalNext = this.querySelector('.swiper-tab_global-next');

    if (globalPrev) {
      globalPrev.addEventListener('click', () => this.slideActivePanel('prev'));
      globalPrev.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.slideActivePanel('prev'); } });
    }
    if (globalNext) {
      globalNext.addEventListener('click', () => this.slideActivePanel('next'));
      globalNext.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.slideActivePanel('next'); } });
    }
  }

  slideActivePanel(direction) {
    const activePanel = this.tabPanels.find(p => p.classList.contains('active'));
    if (!activePanel) return;
    const slider = activePanel.querySelector('.swiper');
    if (!slider || !slider.swiper) return;
    direction === 'prev' ? slider.swiper.slidePrev() : slider.swiper.slideNext();
  }

  activate(index) {

    this.tabButtons.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle("active", active);
    });

    this.tabPanels.forEach((panel, i) => {
      const active = i === index;

      if (active) {
        panel.hidden = false;
        panel.offsetHeight;
        panel.classList.add("active");
      } else {
        panel.classList.remove("active");

        panel.addEventListener(
          "transitionend",
          () => {
            if (!panel.classList.contains("active")) {
              panel.hidden = true;
            }
          },
          { once: true }
        );
      }

    });

    setTimeout(() => {
      const activePanel = this.tabPanels[index];
      if (!activePanel) return;

      activePanel.querySelectorAll('product-card-block').forEach(el => {
        el.mainSwiper?.update();
        el.thumbSwiper?.update();
      });

      window.whenSwiperReady(() => {
        activePanel.querySelectorAll('.feat_section_container.swiper').forEach(slider => {
          const columnsVar = parseInt(getComputedStyle(slider).getPropertyValue('--columns').trim(), 10);
          const desktopSlides = Number.isNaN(columnsVar) ? 4 : Math.max(columnsVar, 1);

          if (slider.swiper) {
            slider.swiper.params.breakpoints ||= {};

            slider.swiper.params.breakpoints[1300] = {
              slidesPerView: desktopSlides
            };

            slider.swiper.update();
            return;
          }

          new Swiper(slider, {
            slidesPerView: 4,
            spaceBetween: 16,
            navigation: {
              nextEl: slider.querySelector('.swiper-main-button-next'),
              prevEl: slider.querySelector('.swiper-main-button-prev'),
            },
              scrollbar: {
              el: slider.querySelector('.swiper-scrollbar'),
              draggable: true,
              snapOnRelease: true,
            },

            observer: true,
            observeParents: true,
            breakpoints: {
              0: { slidesPerView: 1 },
              380: { slidesPerView: 1.3 },
              640: { slidesPerView: 2 },
              900: { slidesPerView: 3 },
              1300: { slidesPerView: desktopSlides }
            }
          });
        });
      });

      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });

    }, 100);
  }
}

customElements.define("collection-tabs", CollectionTabs);

  

  class PdpMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.videos = [];
    this.iframes = [];
    this.cleanupResize = null;
    this._initialized = false;
    this.handleThumbNavResize = this.updateThumbNavigationAxis.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.mainSlider = this.querySelector(".pdp-gallery-main");
    this.thumbSlider = this.querySelector(".pdp-gallery-thumbs");
    this.thumbSliderOuter = this.querySelector(".pdp-gallery-thumbs-shell");
    this.galleryWrapper = this;

    if (!this.mainSlider || !this.thumbSlider) return;

    window.whenSwiperReady(() => this.init());
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleThumbNavResize);
    if (this.cleanupResize) this.cleanupResize();
  }

  init() {
    if (this.swiper || this.thumbsSwiper) return;

    this.isVertical = this.hasAttribute("data-pdp-axis") &&
                  this.getAttribute("data-pdp-axis") !== "false";


    this.thumbsSwiper = new Swiper(this.thumbSlider, {
      direction: this.isVertical ? "vertical" : "horizontal",
      slidesPerView: this.isVertical ? 3 : 5,
      spaceBetween: 15,
      watchSlidesProgress: true, 
      navigation: {
        nextEl: this.thumbSliderOuter.querySelector(".swiper-button-next"),
        prevEl: this.thumbSliderOuter.querySelector(".swiper-button-prev"),
      },
      breakpoints: {
        0: {
          slidesPerView: 4,
          direction: "horizontal",
        }, 
        900: {
          slidesPerView: this.isVertical ? 3 : 5,
          direction: this.isVertical ? "vertical" : "horizontal",
          spaceBetween: this.isVertical ? 20 : 15,
        },
      },
    });

    this.swiper = new Swiper(this.mainSlider, {
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 800,
      autoHeight: true,
      allowTouchMove: false,
      simulateTouch: false,
      navigation: {
        nextEl: this.mainSlider.querySelector(".swiper-button-next"),
        prevEl: this.mainSlider.querySelector(".swiper-button-prev"),
      },
      thumbs: { swiper: this.thumbsSwiper },
      on: {
        slideChange: () => this.pauseAllMedia(),
      },
    });

    this.collectMedia();
    this.bindMediaHandlers();
    this.setupThumbnailClick();
    this.observeHeight();

    this.dispatchEvent(
      new CustomEvent("media-slider-ready", { detail: this.swiper })
    );

    this.updateThumbNavigationAxis();
    window.addEventListener("resize", this.handleThumbNavResize);
  }

  updateThumbNavigationAxis() {
    if (!this.thumbSlider) return;

    const useVerticalThumbNav = this.isVertical && window.innerWidth >= 900;
    this.thumbSlider.classList.toggle("pdp-thumb-rail--vertical", useVerticalThumbNav);
    this.thumbSlider.classList.toggle("pdp-thumb-rail--horizontal", !useVerticalThumbNav);
  }

  collectMedia() {
    this.videos = [...this.querySelectorAll("video")];
    this.iframes = [
      ...this.querySelectorAll(
        "iframe[src*='youtube'], iframe[src*='vimeo']"
      ),
    ];
  }

  pauseAllMedia(except = null) {
    this.videos.forEach(v => v !== except && v.pause());

    this.iframes.forEach(iframe => {
      if (iframe === except) return;
      iframe.contentWindow?.postMessage(
        iframe.src.includes("youtube")
          ? JSON.stringify({ event: "command", func: "pauseVideo", args: [] })
          : JSON.stringify({ method: "pause" }),
        "*"
      );
    });

    this.querySelectorAll("pdp-product-model").forEach(model => {
      if (model !== except && model.modelViewerUI) {
        model.modelViewerUI.pause();
      }
    });
  }
  slideToMediaPosition(position) {
  if (!this.swiper || !position) return;
  
  const slides = [
    ...this.querySelectorAll(
      ".pdp-gallery-main .pdp-media-frame"
    ),
  ];

  const index = slides.findIndex(
    slide => slide.dataset.position == position
  );

  if (index === -1) return;

  this.pauseAllMedia(); 
  this.swiper.slideTo(index); 
  this.thumbsSwiper?.slideTo(index); 
}

  bindMediaHandlers() {
    this.videos.forEach(video => {
      const wrapper = video.closest(".pdp-media-video");
      const overlay = wrapper?.querySelector(".video_overlay");
      const btn = wrapper?.querySelector(".icon_wrapper");

      const play = e => {
        e.preventDefault();
        e.stopPropagation();
        this.pauseAllMedia();
        video.play();
      };

      overlay?.addEventListener("click", play);
      btn?.addEventListener("click", play);

      video.addEventListener("play", () => {
        btn && (btn.style.display = "none");
        overlay && (overlay.style.display = "none");
      });

      video.addEventListener("pause", () => {
        btn && (btn.style.display = "flex");
        overlay && (overlay.style.display = "block");
      });
    });
  }

  setupThumbnailClick() {
    this.querySelectorAll(
      ".pdp-gallery-thumbs .swiper-slide"
    ).forEach(slide => {
      slide.addEventListener("click", () => {
        const pos =
          slide.querySelector(".pdp-media-frame")?.dataset.position;
        if (!pos) return;

        const slides = [
          ...this.querySelectorAll(
            ".pdp-gallery-main .pdp-media-frame"
          ),
        ];

        const index = slides.findIndex(
          s => s.dataset.position === pos
        );

        if (index !== -1) {
          this.pauseAllMedia();
          this.swiper.slideTo(index);
        }
      });
    });
  }

  
  observeHeight() {
  if (!this.isVertical) return;

  const wrapper = this.mainSlider?.querySelector(".swiper-wrapper");
  if (!wrapper) return; 

  const updateFromStyle = () => {
    const height = wrapper.style.height;
    if (!height) return;
    const numericHeight = parseFloat(height); 
    const newHeight = numericHeight - 130;
    this.style.setProperty("--pdp-gallery-match", newHeight  + "px");
  };

  requestAnimationFrame(updateFromStyle);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes" && mutation.attributeName === "style") {
        updateFromStyle();
      }
    }
  });

  observer.observe(wrapper, {
    attributes: true,
    attributeFilter: ["style"]
  });

  this.cleanupResize = () => observer.disconnect();
}

  disconnectedCallback() {
    this.cleanupResize?.();
    this.swiper?.destroy(true, true);
    this.thumbsSwiper?.destroy(true, true);
    this.swiper = null;
    this.thumbsSwiper = null; 
    this._initialized = false;
  } 
}

customElements.define("pdp-media-gallery", PdpMediaGallery);


class VariantSelector extends HTMLElement {
  constructor() {
    super();    
  }

  connectedCallback() {
    this.addEventListener('change', (e) => {
      this.selectedOptions = this.getSelectedOptions;
      
      this.dispatchEvent(
        new CustomEvent('variant-change', {
          detail: {
            selectedOptions: this.selectedOptions
          },
          bubbles: true
        })
      )
    })
  }

  get getSelectedOptions() {
      const selectValues = Array.from(this.querySelectorAll('select')).map((select) => {
        return select.selectedOptions[0].dataset.value;
      })

      const radioValues = Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((input) => {
        return input.dataset.value;
      })

      return [...selectValues, ...radioValues];
  }
}

customElements.define('pdp-variant-selector', VariantSelector);


class pdpShell extends HTMLElement {
    constructor() {
        super();
        this.originalSectionId = this.dataset.mainSectionId;
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;
        this.isMainProduct = this.dataset.mainProduct;
        this.btn = this.querySelector('.pdp-form-submit');
        this.baseUrl = this.dataset.baseUrl;
        this.rootSection = this.closest('.pdp-product-shell');
        this.responseCache = new Map();
        this.abortController = null;
        this.lastRequestId = 0;
    }

    connectedCallback() {
        this.addEventListener('variant-change', this.onVariantChange.bind(this));
        this.addEventListener('quantity-change', this.onQuantityChange.bind(this));
    }

    onQuantityChange(e) {
        const value = e.detail.value;
        const baseInput = this.querySelector('input[name="quantity"]');
        if (baseInput) {
            baseInput.value = value;
        }
    }

    onVariantChange(e) {
        const selectedOptions = e.detail.selectedOptions;

        this.requestURL = this.buildURL(selectedOptions);

      const requestId = ++this.lastRequestId;
      this.loadInfo(this.requestURL, requestId);
    }

    buildURL(options) {
        const params = [];
        if (options.length) {
            params.push(`option_values=${options.join(',')}`);
        }

      const targetSectionId = this.dataset.originalSectionId || this.sectionId;
      if (targetSectionId) {
        params.push(`section_id=${targetSectionId}`);
      }

      return `${this.productUrl}?${params.join('&')}`;
    }

    loadInfo(url, requestId) {
      if (this.responseCache.has(url)) {
        const cached = this.responseCache.get(url);
        const html = new DOMParser().parseFromString(cached, 'text/html');
        this.updateContent(html);
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      fetch(url, { signal: this.abortController.signal })
        .then((res) => res.text())
        .then((htmlString) => {
          if (requestId !== this.lastRequestId) return;

          this.responseCache.set(url, htmlString);
          const html = new DOMParser().parseFromString(htmlString, 'text/html');
          this.updateContent(html);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          console.error('Variant update failed:', error);
        });
    }

    updateContent(html) {
        const originalSectionId = this.dataset.originalSectionId;
        const container = html.querySelector('pdp-product-shell');

        if (this.sectionId.includes('quick-add')) {
            const newSectionId = `quick-add-${originalSectionId}`;
            if (!container) return;

            container.innerHTML = container.innerHTML.replaceAll(originalSectionId, newSectionId);
        }

        container.dataset.sectionId = this.sectionId;

        const oldPrice = this.querySelector(`#Price-${this.sectionId}`);
        const newPrice = html.querySelector(`#Price-${this.sectionId}`);

        if (oldPrice && newPrice) {
            oldPrice.innerHTML = newPrice.innerHTML;
        }

        const oldSKU = this.querySelector(`#SKU-${this.sectionId}`)
        const newSKU = html.querySelector(`#SKU-${this.sectionId}`)

        if (oldSKU && newSKU) {
            oldSKU.innerHTML = newSKU.innerHTML;
        }

        const oldVariantPicker = this.querySelector(`#variant_selector-${this.sectionId}`);
        const newVariantPicker = html.querySelector(`#variant_selector-${this.sectionId}`);

        if (oldVariantPicker && newVariantPicker) {
            oldVariantPicker.innerHTML = newVariantPicker.innerHTML;
        }

        const oldVariantJSON = this.querySelector('[data-selected-variant]');
        const newVariantJSON = html.querySelector('[data-selected-variant]');

        if (oldVariantJSON && newVariantJSON) {
            oldVariantJSON.textContent = newVariantJSON.textContent;
        }

        const oldStackMedia = this.querySelector(`#FeaturedMedia-${this.sectionId}`);
        const newStackMedia = html.querySelector(`#FeaturedMedia-${this.sectionId}`);

        if (newStackMedia && oldStackMedia) {
            oldStackMedia.innerHTML = newStackMedia.innerHTML;
            const gallery = oldStackMedia.closest("stack-gallery");

              if (gallery) { 
                gallery.refresh();
              }
        }

        const oldQuantity = this.querySelector(`#Quantity-${this.sectionId}`);
        const newQuantity = html.querySelector(`#Quantity-${this.sectionId}`);

        if (oldQuantity && newQuantity) {
            oldQuantity.innerHTML = newQuantity.innerHTML;
        }

        const oldInventory = this.querySelector(`#Inventory-${this.sectionId}`);
        const newInventory = html.querySelector(`#Inventory-${this.sectionId}`);

        if (oldInventory && newInventory) {
            oldInventory.innerHTML = newInventory.innerHTML;
        }

        const oldPickup = this.querySelector(`#Pickup-${this.sectionId}`);
        const newPickup = html.querySelector(`#Pickup-${this.sectionId}`);

        if (newPickup && oldPickup) {
            oldPickup.innerHTML = newPickup.innerHTML;
        }
 
        const oldBtn = this.querySelector('.pdp-atc-shell'); 
        const newBtn = html.querySelector('.pdp-atc-shell');
 
        if (oldBtn && newBtn) {
            oldBtn.replaceWith(newBtn.cloneNode(true));
        } 


        const isInsideDrawer = this.closest('.instant-product-view-wrap');

        if (
            this.dataset.updateUrl === 'true' &&
            !isInsideDrawer
        ) {
            this.updateURL(html);
        }
        this.setMainInput(html);

        const variantData = this.extractVariantData(html);

        if (variantData?.featured_media?.position) {
            const gallery = this.querySelector('pdp-media-gallery');

            if (gallery && typeof gallery.slideToMediaPosition === 'function') {
                gallery.slideToMediaPosition(variantData.featured_media.position);
            }
        }


    }

    extractVariantData(html) {
        const container = html.querySelector('[data-selected-variant]');
        if (!container) return;

        return JSON.parse(container.textContent);
    }

    updateURL(html) {
        const variantData = this.extractVariantData(html);
        if (!variantData) return;

        window.history.replaceState(null, {}, `${this.productUrl}?variant=${variantData.id}`);

    }

    
    setMainInput(html) {
        const variantData = this.extractVariantData(html);
        if (!variantData) return;

        const input = this.querySelector('input[name="id"]');
        if (!input) return;

        input.value = variantData.id;
    }

}

customElements.define('pdp-product-shell', pdpShell);


class StackMedia extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    this._initialized = true;
    this.init();
  }

  disconnectedCallback() {
    this._initialized = false;
  }

  init() {
    this.collectMedia();
    this.bindMediaHandlers();
  }

  refresh() {
    this.init();
  }

  collectMedia() {
    this.videos = [...this.querySelectorAll("video")];
    this.iframes = [
      ...this.querySelectorAll(
        'iframe[src*="youtube"], iframe[src*="vimeo"]'
      ),
    ];
    this.models = [...this.querySelectorAll("pdp-product-model")];
  }

  bindMediaHandlers() {
    /* ---------------- Hosted videos ---------------- */

    this.videos.forEach((video) => {
      if (video.dataset.stackBound) return;
      video.dataset.stackBound = "true";

      const wrapper = video.closest(
        ".pdp_stack_shell_featured_video, .pdp_stack_shell_thumbnail_video"
      );

      if (!wrapper) return;

      const btn = wrapper.querySelector(".play_video_icon_button");
      const overlay = wrapper.querySelector(".pdp_video_overlay");

      if (!overlay) return;

      btn?.addEventListener("click", (e) => {
        e.stopPropagation();

        if (video.paused) {
          this.pauseAllMedia(video);
          video.play();
        } else {
          video.pause();
        }
      });

      wrapper.addEventListener("click", (e) => {
        if (e.target.closest(".play_video_icon_button")) return;

        if (video.paused) {
          this.pauseAllMedia(video);
          video.play();
        }
      });

      video.addEventListener("play", () => {
        overlay.style.pointerEvents = "none";
        btn?.classList.add("hide");
        btn?.setAttribute("aria-hidden", "true");
      });

      video.addEventListener("pause", () => {
        overlay.style.pointerEvents = "all";
        btn?.classList.remove("hide");
        btn?.removeAttribute("aria-hidden");
      });

      video.addEventListener("ended", () => {
        overlay.style.pointerEvents = "all";
        btn?.classList.remove("hide");
        btn?.removeAttribute("aria-hidden");
      });

      wrapper.addEventListener("focusout", (e) => {
        if (!wrapper.contains(e.relatedTarget) && !video.paused) {
          this.pauseAllMedia();
        }
      });
    });

    /* ---------------- External Videos ---------------- */

    this.iframes.forEach((iframe) => {
      if (iframe.dataset.stackBound) return;
      iframe.dataset.stackBound = "true";

      const wrapper = iframe.closest(
        ".pdp_stack_shell_featured_external_video, .main_product_thumbnail_external_video"
      );

      if (!wrapper) return;

      const overlay = wrapper.querySelector(".pdp_video_overlay");

      if (!overlay) return;

      overlay.addEventListener("click", () => {
        overlay.style.pointerEvents = "none";

        this.pauseAllMedia(iframe);

        iframe.contentWindow?.postMessage(
          iframe.src.includes("youtube")
            ? JSON.stringify({
                event: "command",
                func: "playVideo",
                args: [],
              })
            : JSON.stringify({
                method: "play",
              }),
          "*"
        );
      });
    });

    /* ---------------- Models ---------------- */

    this.models.forEach((model) => {
      if (model.dataset.stackBound) return;
      model.dataset.stackBound = "true";

      model.addEventListener("click", () => {
        this.pauseAllMedia(model);
      });
    });
  }

  pauseAllMedia(except = null) {
    this.videos.forEach((video) => {
      if (video === except) return;

      video.pause();

      const wrapper = video.closest(
        ".pdp_stack_shell_featured_video, .pdp_stack_shell_thumbnail_video"
      );

      wrapper
        ?.querySelector(".play_video_icon_button")
        ?.classList.remove("hide");

      wrapper
        ?.querySelector(".play_video_icon_button")
        ?.removeAttribute("aria-hidden");

      const overlay = wrapper?.querySelector(".pdp_video_overlay");

      if (overlay) {
        overlay.style.pointerEvents = "all";
      }
    });

    this.iframes.forEach((iframe) => {
      if (iframe === except) return;

      const wrapper = iframe.closest(
        ".pdp_stack_shell_featured_external_video, .main_product_thumbnail_external_video"
      );

      const overlay = wrapper?.querySelector(".pdp_video_overlay");

      if (overlay) {
        overlay.style.pointerEvents = "all";
      }

      iframe.contentWindow?.postMessage(
        iframe.src.includes("youtube")
          ? JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: [],
            })
          : JSON.stringify({
              method: "pause",
            }),
        "*"
      );
    });

    this.models.forEach((model) => {
      if (model === except) return;

      model.modelViewerUI?.pause?.();
    });
  }
}

customElements.define("stack-gallery", StackMedia);

class productQuantity extends HTMLElement {
  constructor() {
    super();
    this.minusBtn = this.querySelector('[data-minus]');
    this.input = this.querySelector('input[type="text"]');
    this.plusBtn = this.querySelector('[data-plus]');
    this.value = Number(this.input.value); 
    this.step = Number(this.input.getAttribute('step')) || 1;
    this.min = this.input.dataset.min || 1;
    this.max = this.input.dataset.max;
  }

  connectedCallback() {
    this.minusBtn.addEventListener('click', this.handleChange.bind(this));
    this.plusBtn.addEventListener('click', this.handleChange.bind(this));
    const debouncedInputandler = this._debounce((e) => this.handleInput(e), 1000)
    this.input.addEventListener('input', debouncedInputandler);
  }

  setBtnDisabled(btn, isDisabled) {
    if(isDisabled) {
      btn.setAttribute('disabled', '');
    } else {
      btn.removeAttribute('disabled');
    }

    btn.setAttribute('tabindex', isDisabled ? '-1' : '0');
  }

  handleChange(e) {
    if(e.currentTarget.hasAttribute('data-plus')) {
      this.value = this.value + this.step;
      this.input.value = this.value;
    } else {
      this.value = this.value - this.step;
      this.input.value = this.value;
    }

    if(this.min) {
      this.setBtnDisabled(this.minusBtn, this.value <= this.min);
    }

    if(this.max) {
      this.setBtnDisabled(this.plusBtn, this.value >= this.max);
    }

    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: {
          value: this.value
        },
        bubbles: true
      })
    )
  }

  handleInput(e) {
    this.value = Number(this.input.value);
    if(this.value == 0) {
      this.value = 1;
      this.input.value = 1;
    }

    if(this.min) {
      this.setBtnDisabled(this.minusBtn, this.value <= this.min);
    }

    if(this.max) {
      this.setBtnDisabled(this.plusBtn, this.value >= this.max);
    }

    this.dispatchEvent(
      new CustomEvent('quantity-change', {
        detail: {
          value: this.value
        },
        bubbles: true
      })
    )

  }

  _debounce(fn, wait = 1000) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('product-quantity', productQuantity);


class BuyNowForm extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.form = this.querySelector('form.product_form');
    this.button = this.querySelector('.buy_now_button');

    if (!this.form || !this.button) return;

    this.icon = this.button.querySelector('.icon_wrapper') || this.button.querySelector('.atc_text');
    this.mainInput = this.querySelector('input[name="id"]');
    this.cart = document.querySelector('theme-cart-drawer');
    this.loader = this.button.querySelector('.form_loader');
    this.info = this.closest('product-info');
    if (this.info) {
      this.giftWrap = this.info.querySelector('#product_gift_wrapp');
    }

    this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(e) {
    e.preventDefault();
    if (this.icon && this.loader) {
      this.icon.classList.add('hidden');
      this.loader.classList.remove('hidden');
    }

    const formData = new FormData(this.form);
    const payload = {};
    for (const [key, value] of formData.entries()) {
    // Detect line item properties
    const propertyMatch = key.match(/^properties\[(.+)\]$/);
    if (propertyMatch) {
      if (!payload.properties) payload.properties = {};
      payload.properties[propertyMatch[1]] = value;
    } else {
      payload[key] = value;
    }
  }
   // const payload = Object.fromEntries(formData.entries());

   const items = [payload];
   
   if(this.giftWrap && this.giftWrap.checked) {
    const giftWrapId = this.giftWrap.value;
    items.push({
      id: giftWrapId,
      quantity: 1,
      parent_id: this.mainInput.value,
      properties: {
        for: `${this.mainInput.dataset.title} (${this.mainInput.dataset.variantTitle})`
      }
    })
   }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    })
    .then(async (res) =>{
      const data = await res.json();
      if(!res.ok) {
        let message = '';

        if(data.errors) {
          const humanizeField = (field) => {
            const match = String(field).match(/^properties\[(.+)\]$/);
            const raw = match ? match[1] : field;
            return String(raw)
              .replace(/[_-]+/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
              .replace(/\b\w/g, (c) => c.toUpperCase());
          };

          Object.entries(data.errors).forEach(([field, messages]) => {
          const label = humanizeField(field);
          const fieldMessage = label ? `${label} ${messages[0]}` : messages[0];
          if(Object.keys(data.errors).length > 1) {
              message += `<div>${fieldMessage}</div>`
          } else {
            message = fieldMessage
          }
        });
        } else if(data.description) {
          message = data.description;
        } else if(data.message) {
          message = data.message;
        }

        const quantityError =
          this.querySelector('.product_quantity_error') ||
          this.closest('pdp-product-shell')?.querySelector('.product_quantity_error') ||
          this.closest('product-card-block')?.querySelector('.product_quantity_error');

        if (quantityError) {
          quantityError.innerHTML = message;

          quantityError.classList.remove('hidden');

        }

        const errorWrapper = this.querySelector('.product_card_cart_error');
        
        if(errorWrapper) {
          errorWrapper.innerHTML = message;
          errorWrapper.classList.add('show');

          setTimeout(() => {
            errorWrapper.classList.remove('show');
          }, 2000)
        }      
        
       
        const rawCartData = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-count,main-cart').then((res) => res.json());        
      const cartData = {          
          'sections': rawCartData,
          'message': message,          
        }

      document.dispatchEvent(
        new CustomEvent('cart-update', {
          detail: cartData,
          bubbles: true
        })
      )

        throw new Error(data.description || 'Error add to cart');
      }
      return data;
    })
    .then(async (data) => {
      if(this.cart) {
        const rawCartData = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-count,main-cart').then((res) => res.json());        
        const cartData = {
            'sections': rawCartData,
            'button': this.button
          }

        document.dispatchEvent(
          new CustomEvent('cart-update', {
            detail: cartData,
            bubbles: true
          })
        )
      } else {
        window.location.href = window.Shopify.routes.root + 'cart'
      }
    })
    .finally(() => {
      this.icon.classList.remove('hidden');
      this.loader.classList.add('hidden')
    })
  }
}

customElements.define('buy-now-form', BuyNowForm);



class ThemeCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.loader = this.querySelector('.cart__loader');
  }

  connectedCallback() {
    document.addEventListener('cart-update', this.cartUpdate.bind(this));
  }

  cartUpdate(e) {
    // CART DRAWER UPDATE
    let tempDiv = document.createElement('div');
    let newDrawer = e.detail.sections['cart-drawer'];
    let error = e.detail.message;
    let button = e.detail.button;
    
    if(newDrawer) {
      tempDiv.innerHTML = newDrawer;
    }
    let cartDrawer = document.querySelector('.theme-cart-drawer__panel');
    if(cartDrawer) {
      const incomingDrawerInner = tempDiv.querySelector('.theme-cart-drawer__panel');
      if (incomingDrawerInner) {
        cartDrawer.innerHTML = incomingDrawerInner.innerHTML;
        if(error && e.detail.line) {
          const lineWrapper = cartDrawer.querySelector(`.quantity_remove_wrap[data-line="${e.detail.line}"]`);
          if(lineWrapper) {
            lineWrapper.querySelector('.cart_quantity_error').textContent = error;
            lineWrapper.querySelector('.cart_quantity_error').classList.remove('hidden');
          }
        }
      }
    }

    // CART COUNT UPDATE
    let temp = document.createElement('div');
    let newCount = e.detail.sections['cart-count'];
    if(newCount) {
      temp.innerHTML = newCount;
    }

    document.querySelector('.cart-count-text').innerHTML = temp.querySelector('.cart-count-text').innerHTML;

    const drawer = document.querySelector('.theme-cart-drawer-shell');
    if(drawer && button) {    
      const productWrapper = document.querySelector('.shopify-section.pdp-product-section');
      if(productWrapper) {
        // productWrapper.style.zIndex = '0'
      }
      openPanel(drawer, button)
    }       
  }
}

defineElementIfNeeded('theme-cart-drawer', ThemeCartDrawer);

class ThemeCartRemove extends HTMLElement {
  constructor() {
    super();
    this.link = this.querySelector('a');
    this.line = this.dataset.line;
    this.wrapper = this.closest('theme-cart-drawer') || this.closest('theme-cart-main');
    this.loader = this.wrapper.querySelector?.('.cart__loader');
  }

  connectedCallback() {
    this.link.addEventListener('click', this.handleRemove.bind(this));
  }

  handleRemove(e) {
    e.preventDefault();
    if(this.loader) {
      this.loader.classList.add('active');
    }

    const formData = {
      'line': this.line,
      'quantity': e.currentTarget.dataset.quantity,
      'sections': 'cart-drawer,cart-count,main-cart'
    }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then((res) => res.json())
    .then((data) => {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart-update', {
          detail: data,
          bubbles: true
        })
      )
    })
  }
}

defineElementIfNeeded('theme-cart-remove', ThemeCartRemove);

class ThemeCartMain extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    document.addEventListener('cart-update', this.handleCartPageUpdate.bind(this));
  }

  handleCartPageUpdate(e) {

    const tempCart = document.createElement('div');
    const newCart = e.detail.sections['main-cart'];
    let error = e.detail.message;
   
    if(newCart) {
      tempCart.innerHTML = newCart;
    }


    const oldCart = document.querySelector('theme-cart-main');
    if(oldCart) {
      oldCart.querySelector('.theme-cart-main__body').innerHTML = tempCart.querySelector('.theme-cart-main__body').innerHTML;
      if(error && e.detail.line) {
        const lineWrapper = oldCart.querySelector(`quantity-wrapper[data-line="${e.detail.line}"]`);
        if(lineWrapper) {
          lineWrapper.querySelector('.cart_quantity_error').textContent = error;
          lineWrapper.querySelector('.cart_quantity_error').classList.remove('hidden');
        }        
      }
    }

    // CART COUNT UPDATE
    let temp = document.createElement('div');
    let newCount = e.detail.sections['cart-count'];
    if(newCount) {
      temp.innerHTML = newCount;
    }

    document.querySelector('.cart-count-text').innerHTML = temp.querySelector('.cart-count-text').innerHTML;     
  }
}

defineElementIfNeeded('theme-cart-main', ThemeCartMain);

class QuantityWrapper extends HTMLElement {
  constructor() {
    super();
    this.minusBtn = this.querySelector('[data-minus]');
    this.plusBtn = this.querySelector('[data-plus]');
    this.input = this.querySelector('input');   
    this.line = this.dataset.line; 
    this.wrapper = this.closest('theme-cart-drawer') || this.closest('theme-cart-main');
    this.loader = this.wrapper.querySelector('.cart__loader');
  }

  connectedCallback() {
    this.plusBtn.addEventListener('click', this.handleChange.bind(this));
    this.minusBtn.addEventListener('click', this.handleChange.bind(this));
    const debouncedHandler = this._debounce((e) => this.handleInput(e), 1000)
    this.input.addEventListener('input', debouncedHandler);
  }

  setBtnDisabled(btn, isDisabled) {
    if(isDisabled) {
      btn.setAttribute('disabled', '');
    } else {
      btn.removeAttribute('disabled');
    }

    btn.setAttribute('tabindex', isDisabled ? '-1' : '0');
  }

  getValue(e) {
    if(e.currentTarget.hasAttribute('data-plus')) {
      let value = parseInt(this.input.value);
      value = value + 1;
      this.setBtnDisabled(this.minusBtn, value == 1);

      this.input.value = value;
      return value;
    } else {
      let value = parseInt(this.input.value);
      value = value - 1;
      this.setBtnDisabled(this.minusBtn, value == 1);

      this.input.value = value;
      return value;
    }
  }

  handleChange(e) {
    e.preventDefault();
    let value = this.getValue(e);
    
    if(this.loader) {
      this.loader.classList.add('active');
    }

    const formData = {
      'line': this.line,
      'quantity': value,
      'sections': 'cart-drawer,cart-count,main-cart'
    }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(async (res) => {
      const data = await res.json();

      if(!res.ok) {        
        const rawCartData = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-count,main-cart').then((res) => res.json());        
        const cartData = {
          'line': this.line,
          'message': data.message || data.description,
          'sections': rawCartData,          
        }

        document.dispatchEvent(
          new CustomEvent('cart-update', {
            detail: cartData,
            bubbles: true
          })
        )
        throw new Error(data.description || data.message || 'Request failed');        
      }

      return data;
    })
    .then((cartData) => {          
      document.documentElement.dispatchEvent(
        new CustomEvent('cart-update', {
          detail: cartData,          
          bubbles: true
        })
      )
    })
  }

  handleInput(e) {
    e.preventDefault();

    if(this.loader) {
      this.loader.classList.add('active');
    }

    const formData = {
      'line': this.line,
      'quantity': e.target.value,
      'sections': 'cart-drawer,cart-count,main-cart'
    }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(async (res) => {
      const data = await res.json();

      if(!res.ok) {        
        const rawCartData = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-count,main-cart').then((res) => res.json());        
        const cartData = {
          'line': this.line,
          'message': data.message || data.description,
          'sections': rawCartData
        }

        document.dispatchEvent(
          new CustomEvent('cart-update', {
            detail: cartData,
            bubbles: true
          })
        )
        throw new Error(data.description || data.message || 'Request failed');        
      }

      return data;
    })
    .then((cartData) => {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart-update', {
          detail: cartData,
          bubbles: true
        })
      ) 
    })
  }

  _debounce(fn, wait = 1000) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
} 

customElements.define('quantity-wrapper', QuantityWrapper);
  

class DiscountField extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.inputValue = this.input.value;
        this.remove = this.querySelector('[data-discount-remove]');
        this.error = this.querySelector('.cart_discount_error');
        this.wrapper = this.closest('theme-cart-drawer') || this.closest('theme-cart-main');
        this.loader = this.wrapper.querySelector('.cart__loader');
  }

  connectedCallback() {
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
        if(this.remove) {
            this.remove.addEventListener('click', this.handleRemove.bind(this));
        }
    }

    handleInput(e) {
        e.preventDefault();
        this.inputValue = this.input.value;
    }

    handleFormSubmit(e) {
      e.preventDefault();

      if(this.loader) {
        this.loader.classList.add('active');
      }

      const formData = {
        discount: this.inputValue,
        sections: 'cart-drawer,cart-count,main-cart'
      }

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': "application/json"
        },
        body: JSON.stringify(formData)
      })
      .then((res) => res.json())
      .then((cartData) => {
        if(cartData.discount_codes[0].applicable == false) {
          if(this.error) {
            this.error.classList.remove('hidden');
            this.input.value = "";
          } 

          if(this.loader) {
           this.loader.classList.remove('active');
          }
        } else {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart-update', {
              detail: cartData,
              bubbles: true
            })
          )
        }
      })
    }

    handleRemove(e) {
      e.preventDefault();

      if(this.loader) {
        this.loader.classList.add('active');      
      }  

      const formData = {
        discount: "",
        sections: "cart-drawer,cart-count,main-cart"
      }

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': "application/json",
        },
        body: JSON.stringify(formData)
      })
      .then((res) => res.json())
      .then((cartData) => {
        document.dispatchEvent(
          new CustomEvent('cart-update', {
            detail: cartData,
            bubbles: true
          })
        )
      })
    }
}

customElements.define('theme-discount', DiscountField);

if (!customElements.get("theme-cart-note")) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.textarea = this.querySelector("textarea[name='note']");
      this.debounceTimer = null;
      this.debouncecascade = 500;
    }

    connectedCallback() {
      if (!this.textarea) return;

      this.textarea.addEventListener("input", this.handleInput.bind(this));
    }

    disconnectedCallback() {
      if (!this.textarea) return;
      this.textarea.removeEventListener("input", this.handleInput);
    }

    handleInput(e) {
      const value = e.target.value;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.updateCartNote(value, e);
      }, this.debouncecascade);
    }

    updateCartNote(note, event) {
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ note }),
      }).then(() => {
        if (window.CartPerformance?.measureFromEvent) {
          CartPerformance.measureFromEvent(
            "note-update:user-action",
            event
          );
        }
      });
    }
  }

  defineElementIfNeeded("theme-cart-note", CartNote);
}


Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};
 

class ProductCardBlock extends HTMLElement {
  constructor() {
    super();

    this.card = this.querySelector('.collection-product-card');

    this.mainSwiperEl = this.querySelector('.product-media.swiper');
    this.media_card = this.querySelector('.product-media-wrapper');
    this.resizeCleanup = null;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    lazyInit(this, () => {
      window.whenSwiperReady(() => this.init());
    });
  }

  init() {
    this.initSwiper();
    this.bindThumbnailSlider();
    this.bindHoverSlide();
    this.bindFocusCleanup();
    this.bindHoverActive();
  }

  /* ================= MAIN SWIPER ================= */

  initSwiper() {
    if (!this.mainSwiperEl) return;

    this.mainSwiper = new Swiper(this.mainSwiperEl, {
      slidesPerView: 1,
      speed: 1000,
      allowTouchMove: false,
      effect: 'fade',
      fadeEffect: { crossFade: true },

      navigation: {
        nextEl: this.mainSwiperEl.querySelector('.swiper-button-next'),
        prevEl: this.mainSwiperEl.querySelector('.swiper-button-prev')
      },

      pagination: {
        el: this.mainSwiperEl.querySelector('.swiper-pagination'),
        clickable: true
      }
    });
  }

  bindThumbnailSlider() {

      const thumbnails =
        this.querySelectorAll(
          '.card_thumbnail_item'
        );

      if (!thumbnails.length || !this.mainSwiper) {
        return;
      }

      const updateActiveThumbnail = (activeIndex) => {

        thumbnails.forEach((thumb) => {

          const thumbIndex =
            Number(
              thumb.dataset.slideIndex
            );

          thumb.classList.toggle(
            'active',
            thumbIndex === activeIndex
          );

        });

      };

      /* initial active */
      updateActiveThumbnail(
        this.mainSwiper.activeIndex
      );

      thumbnails.forEach((thumb) => {

        thumb.addEventListener('click', () => {

          const index =
            Number(
              thumb.dataset.slideIndex
            );

          this.mainSwiper.slideTo(index);

          updateActiveThumbnail(index);

        });

      });

      /* sync when swiper changes */
      this.mainSwiper.on(
        'slideChange',
        () => {

          updateActiveThumbnail(
            this.mainSwiper.activeIndex
          );

        }
      );

}
  /* ================= HOVER SLIDE ================= */

  bindHoverSlide() {
    if (this.getAttribute('secondary_hover') !== 'true') return;
    if (!this.mainSwiper || !this.media_card) return;

    let hoverTimer;
    let didHoverChangeSlide = false;

    const hasSecondSlide = this.mainSwiper.slides.length > 1;
    if (!hasSecondSlide) return;

    this.media_card.addEventListener('mouseenter', () => {
      const isFirstSlide =
        this.mainSwiper.realIndex === 0 || this.mainSwiper.activeIndex === 0;

      if (!isFirstSlide) {
        didHoverChangeSlide = false;
        return;
      }

      hoverTimer = setTimeout(() => {
        didHoverChangeSlide = true;

        this.mainSwiper.slideToLoop
          ? this.mainSwiper.slideToLoop(1)
          : this.mainSwiper.slideTo(1);
      }, 150);
    });

    this.media_card.addEventListener('mouseleave', () => {
      clearTimeout(hoverTimer);

      if (!didHoverChangeSlide) return;

      this.mainSwiper.slideToLoop
        ? this.mainSwiper.slideToLoop(0)
        : this.mainSwiper.slideTo(0);

      didHoverChangeSlide = false;
    });
  }

  /* ================= UI BEHAVIOR ================= */

  bindFocusCleanup() {
    if (!this.card) return;

    this.card.addEventListener('mouseleave', () => {
      if (this.card.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    });
  }

  bindHoverActive() {
    if (!this.card) return;

    const slider = this.card.closest('.product_block_slider');
    if (!slider) return;

    let timer = null;

    this.card.addEventListener('mouseenter', () => {
      clearTimeout(timer);
      slider.classList.add('active');
    });

    this.card.addEventListener('mouseleave', () => {
      timer = setTimeout(() => slider.classList.remove('active'), 300);
    });
  }

  /* ================= CLEANUP ================= */
 
  disconnectedCallback() {
    this.resizeCleanup?.();
    this.mainSwiper?.destroy(true, true);
    this._initialized = false;
  }
} 

customElements.define('product-card-block', ProductCardBlock);

 
class PageTransition extends HTMLElement {
 
  constructor() {
    super(); 

    window.addEventListener("beforeunload", () => {
      document.body.classList.add("page-loading");

      document.dispatchEvent(new CustomEvent("pageTransitionStart", {
        bubbles: true,
        detail: {
          direction: "out",
          timestamp: Date.now()
        }
      }));
    });

    const initOnLoad = () => {
      document.dispatchEvent(new CustomEvent("pageLoaded", {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

      this.hide();
    };

    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", initOnLoad, { once: true });
    } else {
      initOnLoad();
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        document.body.classList.remove("page-loading");
      }
    });
  }

  hide() {
    this.style.transition = "opacity 1s";
    this.style.opacity = 0;

    setTimeout(() => {
      this.hidden = true;

      document.dispatchEvent(new CustomEvent("pageTransitionEnd", {
        bubbles: true,
        detail: { timestamp: Date.now() }
      }));

    }, 1200);
  }
}

customElements.define("page-load", PageTransition);

var Shopify = Shopify || {};
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = ""; 
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};


// --------------------------- Hero Slider-----------------------------

